/** @jsxImportSource @emotion/react */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react'
import { App } from 'antd'
import req from '@sportninja/common/api/request'
import accountActions from '@sportninja/common/actions/account'
import { useSelector, useDispatch } from 'src/hooks/useRedux'

type CustomError = {
  full_name: string | null
  title: string | null
  email: string | null
  company_name: string | null
  phone_number: string | null
  address_street: string | null
  address_city: string | null
  address_country_id: string | null
  address_province_id: string | null
  address_postal_code: string | null
}

type EditAccountState = {
  // Contact fields (API names)
  full_name: string
  title: string
  email: string
  company_name: string
  phone_number: string
  phone_number_country: string

  // Address fields (API names)
  address_street: string
  address_city: string
  address_country_id: number | undefined
  address_province_id: number | undefined
  address_postal_code: string

  // Address display (for CountryAndProvinceInput)
  address_country_iso: string
  address_province_iso: string

  // UI state
  isLoading: boolean
  error: string | null
  fieldErrors: string[]
  customError: CustomError
}

type EditAccountAction =
  | { type: 'SET_FIELD'; field: string; value: unknown }
  | { type: 'SET_LOADING'; value: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_FIELD_ERRORS'; errors: string[] }
  | { type: 'SET_CUSTOM_ERROR_BY_FIELD'; field: string; message: string | null }
  | { type: 'CLEAR_CUSTOM_ERROR_BY_FIELD'; field: string }
  | { type: 'CLEAR_ALL_ERRORS' }
  | { type: 'INIT_FROM_ACCOUNT'; account: any }

const initialCustomError: CustomError = {
  full_name: null,
  title: null,
  email: null,
  company_name: null,
  phone_number: null,
  address_street: null,
  address_city: null,
  address_country_id: null,
  address_province_id: null,
  address_postal_code: null,
}

const initialState: EditAccountState = {
  full_name: '',
  title: '',
  email: '',
  company_name: '',
  phone_number: '',
  phone_number_country: '',
  address_street: '',
  address_city: '',
  address_country_id: undefined,
  address_province_id: undefined,
  address_postal_code: '',
  address_country_iso: '',
  address_province_iso: '',
  isLoading: false,
  error: null,
  fieldErrors: [],
  customError: initialCustomError,
}

function editAccountReducer(
  state: EditAccountState,
  action: EditAccountAction
): EditAccountState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }

    case 'SET_LOADING':
      return { ...state, isLoading: action.value }

    case 'SET_ERROR':
      return { ...state, error: action.error }

    case 'SET_FIELD_ERRORS':
      return { ...state, fieldErrors: action.errors }

    case 'SET_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: action.message,
        },
        fieldErrors: action.message
          ? [...new Set([...state.fieldErrors, action.field])]
          : state.fieldErrors,
      }

    case 'CLEAR_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: null,
        },
        fieldErrors: state.fieldErrors.filter((e) => e !== action.field),
      }

    case 'CLEAR_ALL_ERRORS':
      return {
        ...state,
        error: null,
        fieldErrors: [],
        customError: initialCustomError,
      }

    case 'INIT_FROM_ACCOUNT':
      return {
        ...state,
        full_name: action.account?.full_name || '',
        title: action.account?.title || '',
        email: action.account?.email || '',
        company_name: action.account?.company_name || '',
        phone_number: action.account?.phone_number || '',
        phone_number_country: action.account?.phone_number_country || '',
        address_street: action.account?.address?.street_1 || '',
        address_city: action.account?.address?.city || '',
        address_country_id: action.account?.address?.country?.id,
        address_province_id: action.account?.address?.province?.id,
        address_postal_code: action.account?.address?.postal_code || '',
        address_country_iso: action.account?.address?.country?.iso_3166_2 || '',
        address_province_iso:
          action.account?.address?.province?.iso_3166_2 || '',
      }

    default:
      return state
  }
}

type EditAccountContextValue = {
  state: EditAccountState
  dispatch: React.Dispatch<EditAccountAction>
  onSubmit: () => Promise<void>
  onCancel: () => void
  setCustomErrorByField: (field: string, message: string | null) => void
  clearCustomErrorByField: (field: string) => void
}

const EditAccountContext = createContext<EditAccountContextValue | null>(null)

export const useEditAccountContext = () => {
  const context = useContext(EditAccountContext)
  if (!context) {
    throw new Error(
      'useEditAccountContext must be used within EditAccountProvider'
    )
  }
  return context
}

type EditAccountProviderProps = {
  children: ReactNode
  isOpen: boolean
  onClose: () => void
}

export const EditAccountProvider: React.FC<EditAccountProviderProps> = ({
  children,
  isOpen,
  onClose,
}) => {
  const [state, dispatch] = useReducer(editAccountReducer, initialState)
  const { message } = App.useApp()
  const reduxDispatch = useDispatch()
  const account = useSelector((s: any) => s.account._meAccount)
  const me = useSelector((s: any) => s.auth.user)

  // Initialize form from account data when modal opens
  React.useEffect(() => {
    if (isOpen && account) {
      dispatch({ type: 'INIT_FROM_ACCOUNT', account })
      dispatch({ type: 'CLEAR_ALL_ERRORS' })
    }
  }, [isOpen, account])

  const setCustomErrorByField = useCallback(
    (field: string, errorMessage: string | null) => {
      dispatch({
        type: 'SET_CUSTOM_ERROR_BY_FIELD',
        field,
        message: errorMessage,
      })
    },
    []
  )

  const clearCustomErrorByField = useCallback((field: string) => {
    dispatch({ type: 'CLEAR_CUSTOM_ERROR_BY_FIELD', field })
  }, [])

  const setCustomErrors = useCallback(
    (invalid_fields: Record<string, string | string[]>) => {
      const normalizeError = (error: string | string[]): string => {
        return Array.isArray(error) ? error.join(', ') : error
      }

      Object.entries(invalid_fields).forEach(([field, error]) => {
        setCustomErrorByField(field, normalizeError(error))
      })
    },
    [setCustomErrorByField]
  )

  const onSubmit = useCallback(async () => {
    dispatch({ type: 'CLEAR_ALL_ERRORS' })
    dispatch({ type: 'SET_LOADING', value: true })

    try {
      // 1. Submit contact info
      const contactBody: Record<string, string> = {
        company_name: state.company_name,
        email: state.email,
        full_name: state.full_name,
        title: state.title,
      }

      if (state.phone_number?.trim().length > 0) {
        contactBody.phone_number = state.phone_number
        contactBody.phone_number_country = state.phone_number_country || 'US'
      }

      await req(`/account/${account?.uid}/contact`, {
        method: 'PUT',
        body: JSON.stringify(contactBody),
      })

      // 2. Submit address info via Redux
      reduxDispatch(
        accountActions.updateAccountAddress.request(null, null, account?.uid, {
          address_street: state.address_street,
          address_city: state.address_city,
          address_country_id: state.address_country_id,
          address_province_id: state.address_province_id,
          address_postal_code: state.address_postal_code,
        })
      )

      // Refetch account data to sync UI
      reduxDispatch(
        accountActions.fetchAccount.request(null, null, me?.account_id)
      )
      message.success('Account Updated Successfully')
      onClose()
    } catch (error: unknown) {
      const err = error as {
        invalid_fields?: Record<string, string | string[]>
        message?: string
      }

      if (err.invalid_fields) {
        setCustomErrors(err.invalid_fields)
      } else {
        const errorMessage =
          err.message ||
          (error instanceof Error ? error.message : 'An error occurred')
        dispatch({ type: 'SET_ERROR', error: errorMessage })
      }
    } finally {
      dispatch({ type: 'SET_LOADING', value: false })
    }
  }, [
    state,
    account?.uid,
    me?.account_id,
    reduxDispatch,
    onClose,
    message,
    setCustomErrors,
  ])

  const onCancel = useCallback(() => {
    if (account) {
      dispatch({ type: 'INIT_FROM_ACCOUNT', account })
    }
    dispatch({ type: 'CLEAR_ALL_ERRORS' })
    onClose()
  }, [onClose, account])

  const value: EditAccountContextValue = {
    state,
    dispatch,
    onSubmit,
    onCancel,
    setCustomErrorByField,
    clearCustomErrorByField,
  }

  return (
    <EditAccountContext.Provider value={value}>
      {children}
    </EditAccountContext.Provider>
  )
}

export default EditAccountContext
